import React from 'react';
import { TableModel, ITableColumn, AggregrationType, TableRow } from '../table/table.model';
import { formatNumTwoDig } from 'app/utils';
import { Button } from '../common/Button';
import { Icon } from '../common/Icon';

export interface TreuebonusAgentur {
	agtId: string;
	bnr: string;
	vd: string;
	name: string;
	used: number;
	active: number;
	lastYear: number;
	lastRequest?: {
		date: string;
		amount: number;
		status: 'genehmigt' | 'in_pruefung' | 'abgelehnt';
	};
	historyCount: number;
	allocation: number;
}

interface AmountInputProps {
	value: number;
	onChange: (value: number) => void;
	min: number;
	max: number;
}

const AmountInput: React.FC<AmountInputProps> = ({ value, onChange, min, max }) => {
	const [draft, setDraft] = React.useState(() => formatDraft(value));

	React.useEffect(() => {
		setDraft(formatDraft(value));
	}, [value]);

	function formatDraft(v: number) {
		if (v === 0) return '';
		return (v > 0 ? '+' : '−') + Math.abs(v).toLocaleString('de-DE');
	}

	const commit = (raw: string) => {
		const clean = raw.replace(/[^\d\-−+]/g, '').replace('−', '-');
		let n = parseInt(clean, 10);
		if (isNaN(n)) n = 0;
		n = Math.min(max, Math.max(min, n));
		onChange(n);
		setDraft(formatDraft(n));
	};

	const step = (delta: number) => {
		const next = Math.min(max, Math.max(min, value + delta));
		onChange(next);
	};

	const color = value > 0 ? '#1e8927' : value < 0 ? '#e30613' : '#666';

	return (
		<div className="form-control has-unit-display" style={{ width: '100px' }}>
			<input
				className="input is-small"
				type="text"
				inputMode="numeric"
				value={draft}
				placeholder="0"
				onChange={(e) => setDraft(e.target.value)}
				onBlur={(e) => commit(e.target.value)}
				onKeyDown={(e) => {
					if (e.key === 'Enter') {
						commit(e.currentTarget.value);
						e.currentTarget.blur();
					}
				}}
			/>
			<div className="unit-display">€</div>
		</div>
	);

	// return (
	// 	<div className="field has-addons grid is-col-auto is-gapless">
	// 		<div className="control">
	// 			<Button className="button" onClick={() => step(-500)} disabled={value <= min} title="Verringern">
	// 				<Icon iconClass="minus" />
	// 			</Button>
	// 		</div>
	// 		<div className="control">
	// 			<input
	// 				className="input is-small"
	// 				type="text"
	// 				inputMode="numeric"
	// 				value={draft}
	// 				placeholder="0"
	// 				onChange={(e) => setDraft(e.target.value)}
	// 				onBlur={(e) => commit(e.target.value)}
	// 				onKeyDown={(e) => {
	// 					if (e.key === 'Enter') {
	// 						commit(e.currentTarget.value);
	// 						e.currentTarget.blur();
	// 					}
	// 				}}
	// 			/>
	// 		</div>
	// 		<div className="control">
	// 			<span className="button is-static is-small">€</span>
	// 		</div>
	// 		<div className="control">
	// 			<Button className="button is-small" onClick={() => step(500)} disabled={value >= max} title="Erhöhen">
	// 				<Icon iconClass="plus" />
	// 			</Button>
	// 		</div>
	// 	</div>
	// );
};

const StatusPill: React.FC<{ status: 'genehmigt' | 'in_pruefung' | 'abgelehnt' }> = ({ status }) => {
	const map = {
		genehmigt: { kind: 'is-turquoise', icon: 'check', label: 'Genehmigt' },
		in_pruefung: { kind: 'is-yellow', icon: 'clock', label: 'In Prüfung' },
		abgelehnt: { kind: 'is-red', icon: 'xmark', label: 'Abgelehnt' },
	};
	const { kind, icon, label } = map[status];
	return (
		<span className={`tag ${kind} is-light is-small`}>
			<Icon iconClass={icon} faModifier="fas" />
			<span style={{ marginLeft: 4 }}>{label}</span>
		</span>
	);
};

export class TreuebonusPlanungTableModel extends TableModel<TreuebonusAgentur, string> {
	onAllocationChange?: (agtId: string, value: number) => void;
	onShowHistory?: (agtId: string) => void;
	remainingPool: number = 0;

	constructor() {
		super();
		this.idProperty = 'agtId';
		this.idType = 'string';
		this.sortBy = 'name';
		this.sortAsc = true;

		const cols: ITableColumn<TreuebonusAgentur, string>[] = [];

		cols.push({
			label: 'Agentur',
			path: 'name',
			sortBy: 'name',

			render: (row: TableRow<TreuebonusAgentur>) => (
				<div>
					<strong>{row.data.name}</strong> <br />
					<small className="color-grey">{row.data.agtId}</small>
				</div>
			),
		});

		cols.push({
			label: 'VD',
			path: 'vd',
			sortBy: 'vd',
			render: (row: TableRow<TreuebonusAgentur>) => <span className="tag is-light is-small">{row.data.vd}</span>,
		});

		cols.push({
			label: 'Verbraucht',
			path: 'used',
			sortBy: 'used',
			render: (row: TableRow<TreuebonusAgentur>) => <>{formatNumTwoDig(row.data.used)} €</>,
		});

		cols.push({
			label: 'Aktiv',
			path: 'active',
			sortBy: 'active',
			render: (row: TableRow<TreuebonusAgentur>) => <>{formatNumTwoDig(row.data.active)} €</>,
		});

		cols.push({
			label: 'Vorjahr',
			path: 'lastYear',
			sortBy: 'lastYear',

			render: (row: TableRow<TreuebonusAgentur>) => <span style={{ color: '#999' }}>{formatNumTwoDig(row.data.lastYear)} €</span>,
		});

		cols.push({
			label: 'Letzter Antrag',
			path: 'lastRequest',
			render: (row: TableRow<TreuebonusAgentur>) => {
				if (!row.data.lastRequest) {
					return <span style={{ color: '#999' }}>—</span>;
				}
				return (
					<div>
						<div style={{ marginBottom: 4 }}>{formatNumTwoDig(row.data.lastRequest.amount)} €</div>
						<StatusPill status={row.data.lastRequest.status} />
					</div>
				);
			},
		});

		cols.push({
			label: 'Treuebonus-Anpassung',
			path: 'allocation',
			sortBy: 'allocation',

			render: (row: TableRow<TreuebonusAgentur>) => {
				const value = row.data.allocation;
				const max = value + Math.max(0, this.remainingPool);
				const min = -row.data.active;
				return <AmountInput value={value} onChange={(v) => this.onAllocationChange?.(row.data.agtId, v)} min={min} max={max} />;
			},
			agg: {
				aggType: AggregrationType.sum,
				format: (val) => {
					const sign = val > 0 ? '+' : '';
					return sign + formatNumTwoDig(val) + ' €';
				},
			},
		});

		cols.push({
			label: 'Historie',
			path: 'historyCount',

			render: (row: TableRow<TreuebonusAgentur>) => (
				<Button
					className="button is-small is-text"
					onClick={(e) => {
						e.stopPropagation();
						this.onShowHistory?.(row.data.agtId);
					}}
					title="Historie anzeigen"
				>
					<Icon iconClass="clock-rotate-left" faModifier="far" />
					{row.data.historyCount > 0 && (
						<span className="tag is-primary is-light is-small" style={{ marginLeft: 6 }}>
							{row.data.historyCount}
						</span>
					)}
				</Button>
			),
		});

		this.setCols(cols);
	}
}
